import { PERMISSIONS, type Permission } from './permissions.js';

export const WORK_ORDER_STATUSES = [
  'submitted',
  'under_vgs_review',
  'estimate_in_progress',
  'awaiting_customer_approval',
  'approved',
  'procurement',
  'repair_scheduled',
  'work_in_progress',
  'completed',
  'closed',
  'revision_requested',
  'rejected',
  'on_hold',
  'cancelled',
] as const;

export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  submitted: 'Submitted',
  under_vgs_review: 'Under VGS Review',
  estimate_in_progress: 'Estimate in Progress',
  awaiting_customer_approval: 'Awaiting USPS Approval',
  approved: 'Approved',
  procurement: 'Procurement Stage',
  repair_scheduled: 'Repair Scheduled',
  work_in_progress: 'Work in Progress',
  completed: 'Completed',
  closed: 'Closed',
  revision_requested: 'Revision Requested',
  rejected: 'Rejected',
  on_hold: 'On Hold',
  cancelled: 'Cancelled',
};

/** What each status means in plain words, for people who do not live in the system. */
export const WORK_ORDER_STATUS_MEANINGS: Record<WorkOrderStatus, string> = {
  submitted:
    'Entered into the system — VGS still needs to acknowledge it. Nothing has gone to USPS.',
  under_vgs_review: 'Acknowledged — VGS is looking at the problem; the quote has not been started.',
  estimate_in_progress: 'Quote being prepared — not yet sent to USPS.',
  awaiting_customer_approval: 'Sent to USPS — waiting for the COR/CO to decide.',
  approved: 'USPS approved the price.',
  procurement:
    'USPS approved — the PR/PO (or P-card) is being arranged before the repair is scheduled.',
  repair_scheduled: 'Repair date set — work has not started.',
  work_in_progress: 'Crew is on site.',
  completed: 'Work is done — only the paperwork to close remains.',
  closed: 'Finished and locked.',
  revision_requested: 'USPS sent the quote back with a comment — fix it and resubmit.',
  rejected: 'USPS declined the work.',
  on_hold: 'Paused — resume to return to where it was.',
  cancelled: 'Cancelled.',
};

/** The main workflow path, in the order a work order moves through it. */
export const PRIMARY_WORK_ORDER_STATUSES: readonly WorkOrderStatus[] = [
  'submitted',
  'under_vgs_review',
  'estimate_in_progress',
  'awaiting_customer_approval',
  'approved',
  'procurement',
  'repair_scheduled',
  'work_in_progress',
  'completed',
  'closed',
];

/** Exception states a work order can enter from the main path. */
export const SECONDARY_WORK_ORDER_STATUSES: readonly WorkOrderStatus[] = [
  'revision_requested',
  'rejected',
  'on_hold',
  'cancelled',
];

/** Statuses in which a work order is still being actively worked. */
export const OPEN_WORK_ORDER_STATUSES: readonly WorkOrderStatus[] = [
  'submitted',
  'under_vgs_review',
  'estimate_in_progress',
  'awaiting_customer_approval',
  'approved',
  'procurement',
  'repair_scheduled',
  'work_in_progress',
  'revision_requested',
  'on_hold',
];

export const TERMINAL_WORK_ORDER_STATUSES: readonly WorkOrderStatus[] = [
  'closed',
  'cancelled',
  'rejected',
];

export interface StatusTransition {
  from: WorkOrderStatus;
  to: WorkOrderStatus;
  permission: Permission;
  /** Whether the actor must belong to the contractor organization. */
  contractorOnly: boolean;
  /** Whether a free-text reason is mandatory. */
  reasonRequired: boolean;
}

/**
 * The authoritative work order state machine. This table is also inserted into
 * `work_order_status_transitions` by migration, and a database trigger rejects any
 * status change that is not present there — so the rule is enforced twice.
 */
export const WORK_ORDER_TRANSITIONS: readonly StatusTransition[] = [
  {
    from: 'submitted',
    to: 'under_vgs_review',
    permission: PERMISSIONS.WORK_ORDER_EDIT,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'submitted',
    to: 'cancelled',
    permission: PERMISSIONS.WORK_ORDER_CANCEL,
    contractorOnly: false,
    reasonRequired: true,
  },
  {
    from: 'submitted',
    to: 'on_hold',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: true,
  },

  {
    from: 'under_vgs_review',
    to: 'estimate_in_progress',
    permission: PERMISSIONS.WORK_ORDER_EDIT,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'under_vgs_review',
    to: 'on_hold',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: true,
  },
  {
    from: 'under_vgs_review',
    to: 'cancelled',
    permission: PERMISSIONS.WORK_ORDER_CANCEL,
    contractorOnly: true,
    reasonRequired: true,
  },

  {
    from: 'estimate_in_progress',
    to: 'awaiting_customer_approval',
    permission: PERMISSIONS.PROPOSAL_SUBMIT,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'estimate_in_progress',
    to: 'on_hold',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: true,
  },
  {
    from: 'estimate_in_progress',
    to: 'cancelled',
    permission: PERMISSIONS.WORK_ORDER_CANCEL,
    contractorOnly: true,
    reasonRequired: true,
  },

  {
    from: 'awaiting_customer_approval',
    to: 'approved',
    permission: PERMISSIONS.PROPOSAL_APPROVE,
    contractorOnly: false,
    reasonRequired: false,
  },
  {
    from: 'awaiting_customer_approval',
    to: 'revision_requested',
    permission: PERMISSIONS.PROPOSAL_APPROVE,
    contractorOnly: false,
    reasonRequired: true,
  },
  {
    from: 'awaiting_customer_approval',
    to: 'rejected',
    permission: PERMISSIONS.PROPOSAL_APPROVE,
    contractorOnly: false,
    reasonRequired: true,
  },

  {
    from: 'revision_requested',
    to: 'estimate_in_progress',
    permission: PERMISSIONS.PROPOSAL_MANAGE,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'revision_requested',
    to: 'cancelled',
    permission: PERMISSIONS.WORK_ORDER_CANCEL,
    contractorOnly: true,
    reasonRequired: true,
  },

  {
    from: 'approved',
    to: 'procurement',
    permission: PERMISSIONS.PROPOSAL_APPROVE,
    contractorOnly: false,
    reasonRequired: false,
  },
  {
    from: 'approved',
    to: 'repair_scheduled',
    permission: PERMISSIONS.EXECUTION_MANAGE,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'procurement',
    to: 'repair_scheduled',
    permission: PERMISSIONS.EXECUTION_MANAGE,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'procurement',
    to: 'on_hold',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: true,
  },
  {
    from: 'approved',
    to: 'on_hold',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: true,
  },

  {
    from: 'repair_scheduled',
    to: 'work_in_progress',
    permission: PERMISSIONS.EXECUTION_MANAGE,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'repair_scheduled',
    to: 'on_hold',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: true,
  },

  {
    from: 'work_in_progress',
    to: 'completed',
    permission: PERMISSIONS.COMPLETION_MANAGE,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'work_in_progress',
    to: 'on_hold',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: true,
  },

  {
    from: 'completed',
    to: 'closed',
    permission: PERMISSIONS.WORK_ORDER_CLOSE,
    contractorOnly: true,
    reasonRequired: false,
  },

  {
    from: 'on_hold',
    to: 'submitted',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'on_hold',
    to: 'awaiting_customer_approval',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'on_hold',
    to: 'under_vgs_review',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'on_hold',
    to: 'estimate_in_progress',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'on_hold',
    to: 'approved',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'on_hold',
    to: 'procurement',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'on_hold',
    to: 'repair_scheduled',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'on_hold',
    to: 'work_in_progress',
    permission: PERMISSIONS.WORK_ORDER_HOLD,
    contractorOnly: true,
    reasonRequired: false,
  },
  {
    from: 'on_hold',
    to: 'cancelled',
    permission: PERMISSIONS.WORK_ORDER_CANCEL,
    contractorOnly: true,
    reasonRequired: true,
  },
];

export function findTransition(
  from: WorkOrderStatus,
  to: WorkOrderStatus,
): StatusTransition | undefined {
  return WORK_ORDER_TRANSITIONS.find((t) => t.from === from && t.to === to);
}

export function allowedTransitionsFrom(from: WorkOrderStatus): readonly StatusTransition[] {
  return WORK_ORDER_TRANSITIONS.filter((t) => t.from === from);
}

export const PROPOSAL_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'revision_requested',
  'rejected',
  'superseded',
] as const;

export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  revision_requested: 'Revision Requested',
  rejected: 'Rejected',
  superseded: 'Superseded',
};

/** A proposal version is frozen the moment it leaves draft. */
export const EDITABLE_PROPOSAL_STATUSES: readonly ProposalStatus[] = ['draft'];

export const PRIORITIES = ['emergency', 'urgent', 'routine', 'scheduled'] as const;

/** Status wording for the given work order type: supplies orders are ordered, not scheduled. */
export function workOrderStatusLabel(
  status: WorkOrderStatus,
  kind?: 'repair' | 'supplies',
): string {
  if (kind === 'supplies' && (status === 'repair_scheduled' || status === 'work_in_progress')) {
    return 'Ordered';
  }
  return WORK_ORDER_STATUS_LABELS[status] ?? status;
}

/** How a supplies / consumables order is paid. VGS-internal; never shown to USPS. */
export const SUPPLIES_PAYMENT_METHODS = ['amazon_corp', 'mcmaster_carr', 'pcard'] as const;
export type SuppliesPaymentMethod = (typeof SUPPLIES_PAYMENT_METHODS)[number];
export const SUPPLIES_PAYMENT_METHOD_LABELS: Record<SuppliesPaymentMethod, string> = {
  amazon_corp: 'Amazon Corp',
  mcmaster_carr: 'McMaster-Carr',
  pcard: 'P-card',
};
export type Priority = (typeof PRIORITIES)[number];
export type WorkOrderPriority = Priority;

export const PRIORITY_LABELS: Record<Priority, string> = {
  emergency: 'Emergency',
  urgent: 'Urgent',
  routine: 'Routine',
  scheduled: 'Scheduled',
};

export const COST_CATEGORIES = [
  'labor',
  'materials',
  'equipment',
  'subcontractor',
  'other',
] as const;

export type CostCategory = (typeof COST_CATEGORIES)[number];

export const COST_CATEGORY_LABELS: Record<CostCategory, string> = {
  labor: 'Labor',
  materials: 'Materials',
  equipment: 'Equipment',
  subcontractor: 'Subcontractor',
  other: 'Other',
};

export const APPROVAL_DECISIONS = ['approved', 'rejected', 'revision_requested'] as const;
export type ApprovalDecision = (typeof APPROVAL_DECISIONS)[number];
